import React, { useState, Fragment, useEffect, useMemo, useCallback } from 'react';

import { getTutorials } from '../../../store/tutorials';
import { getCoverData, initialFVICoverData } from '../../../store/coverData';
import { getReportDetails, setItemsPerPage, setLastPhotoNumber } from '../../../store/reportDetails';
import { useAppDispatch, useAppSelector } from '../../../store/configureStore';

import { DEFAULT_REPORT_TITLE, TABLE_ITEM_COUNT_LIMITS, tutorialConstants } from '../../../utils/constants';
import { PDFImageProps, PIDData, User } from '../../../utils/models';
import { getCurrentUser } from '../../../services/authService';
import { matchPhrases, reArrangePhotoRecord, validateWorkOrderNumber } from '../../../utils/utilities';
import { InfraredEquipment } from '../../forms/custom-infrared/infraredEquipmentForm';
import { InfraredResult } from '../../forms/custom-infrared/infraredResultForm';

import Button from '../../themed/button';
import Conditional from '../../common/conditional';
import CustomCoverForm from '../../forms/custom-fvi/coverForm';
import CustomInfraredReport from '.';
import Drawing from '../../forms/custom-lpt/drawing';
import Elevation from '../../themed/instruction-modals/elevation';
import ExecutiveSummaryForm from '../../forms/visual-inspection/executiveSummaryForm';
import FVICoverData from '../../themed/fvi-cover-data';
import FVIStructurePIDImageForm from '../../forms/custom-fvi/fviStructurePIDImageForm';
import LoadingBadge from '../../themed/loading-badge';
import Modal from '../../themed/modal';
import MUTPhotograpicRecord from '../../forms/mut/mutPhotographicRecord';
import InfraredEquipmentItems from './infraredEquipmentItems';
import InfraredResultItems from './infraredResultItems';
import ReportTitleForm from '../../forms/custom-fvi/reportTitleForm';
import SyncTime from '../../blocks/sync-time';
import WarningNotice from '../custom-visual-inspection/warning-notice';
import Zippy from '../../themed/zippy';

import useAutoSaveReport from '../../../hooks/useAutoSave';
import useInspectedItemIDUpdate from '../../../hooks/useInspectedItemIDUpdate';
import useProjectDetails from '../../../hooks/useProjectDetails';

import config from '../../../utils/config';
import toastService from '../../../services/toastService';

const CreateCustomInfraredReport: React.FC = () => {
	const { fviCoverData } = useAppSelector(getCoverData);
	const { isSaving } = useAppSelector(getReportDetails);

	const [previewReport, setPreviewReport] = useState<boolean>(false);
	const [reportTitle, setReportTitle] = useState(DEFAULT_REPORT_TITLE);
	const [coverData, setCoverData] = useState(initialFVICoverData);
	const [executiveSummary, setExecutiveSummary] = useState('');
	const [pidData, setPIDData] = useState<PIDData>({ hasPID: 'yes', pIDNumber: '', pIDImages: [] });
	const [drawing, setDrawing] = useState<PDFImageProps[]>([]);
	const [sketch, setSketch] = useState<PDFImageProps[]>([]);
	const [infraredEquipmentItems, setInfraredEquipmentItems] = useState<InfraredEquipment[]>([]);
	const [infraredResultItems, setInfraredResultItems] = useState<InfraredResult[]>([]);
	const [photographicData, setPhotographicData] = useState<PDFImageProps[]>([]);

	const { projectDetails, error } = useProjectDetails();
	const { activeTutorial } = useAppSelector(getTutorials);
	const { role } = getCurrentUser() as User;

	const reportDetails = useMemo(() => {
		return {
			reportTitle,
			coverData,
			executiveSummary,
			pidData,
			drawing,
			sketch,
			infraredEquipmentItems,
			infraredResultItems,
			photographicData,
			projectDetails,
		};
	}, [projectDetails, coverData, drawing, executiveSummary, pidData, sketch, infraredEquipmentItems, infraredResultItems, photographicData, reportTitle]);

	const autoSave = useAutoSaveReport(reportDetails);
	const dispatch = useAppDispatch();

	const validationMessage = useMemo(() => {
		if (!validateWorkOrderNumber(coverData.workOrderNumber)) {
			return 'Enter a valid work order number!';
		}

		if (!executiveSummary) {
			return 'Executive summary missing!';
		}

		if (matchPhrases(pidData.hasPID, 'yes') && pidData.pIDImages.length === 0) {
			return 'Provide a valid P & ID drawing';
		}
		
		if (infraredEquipmentItems.length === 0) {
			return 'Please provide at least one Infrared equipment';
		}
		
		if (infraredResultItems.length === 0) {
			return 'Please provide at least one Infrared result';
		}
		
		if (drawing.length === 0) {
			return 'Please upload General Drawing!';
		}
		
		if (sketch.length === 0) {
			return 'Please upload Inspection Sketch!';
		}
		
		if (photographicData.length === 0) {
			return 'Please upload photographic report!';
		}
		
		return '';
	}, [coverData, drawing, executiveSummary, pidData, infraredEquipmentItems, infraredResultItems, photographicData, sketch]);

	const togglePreviewReport = useCallback((): void => {
		if (config.enableReportCompletionBeforePublish) {
			if (validationMessage) return toastService.error(validationMessage);
		}
		
		setPreviewReport((p: boolean) => !p);
	}, [validationMessage]);

	const handleSaveAndPreview = useCallback(() => {
		autoSave.saveReport();
		togglePreviewReport();
	}, [autoSave, togglePreviewReport]);
	
	useEffect(() => {
		function initializeCache() {
			const cachedReport = autoSave.oldReportData as typeof reportDetails | null;
			
			if (!cachedReport) {
				if (fviCoverData.coverData.length > 0) setCoverData(fviCoverData);
				
				return;
			}

			setReportTitle(cachedReport.reportTitle);
			setCoverData(cachedReport.coverData?.coverData?.length > 0 ? cachedReport.coverData : fviCoverData);
			setExecutiveSummary(cachedReport.executiveSummary);
			setPIDData(cachedReport.pidData);
			setDrawing(cachedReport.drawing);
			setInfraredEquipmentItems(cachedReport.infraredEquipmentItems);
			setInfraredResultItems(cachedReport.infraredResultItems);
			setPhotographicData(reArrangePhotoRecord(cachedReport.photographicData));
			setSketch(cachedReport.sketch);

			dispatch(setLastPhotoNumber(cachedReport.photographicData.length));
		}

		initializeCache();
	}, [autoSave.oldReportData, dispatch, fviCoverData]);

	useEffect(() => {
		dispatch(setItemsPerPage(TABLE_ITEM_COUNT_LIMITS.PMI));
	}, [dispatch]);

	useInspectedItemIDUpdate(infraredEquipmentItems.map((item) => item.equipmentName));

	return (
		<Fragment>
			<Modal isVisible={previewReport} onClose={togglePreviewReport}>
				<CustomInfraredReport {...reportDetails} />
			</Modal>

			<div className="mb-4">
				<SyncTime lastSynced={autoSave.lastSaved} />
			</div>

			{projectDetails && (
				<FVICoverData
					coverDataId={config.coverDataTemplates.infrared[projectDetails.organisationId]}
					isVisible={projectDetails.inspectionTypeId === config.supportedTemplates.infrared && role.approveReports}
					subtitle={`Update the Cover Data information. This is the default data used on all ${projectDetails.organisationName} ${projectDetails.inspectionTypeName} Reports`}
				/>
			)}

			<Conditional isVisible={validationMessage.length > 0}>
				<WarningNotice title="Report Incomplete" body={validationMessage} />
			</Conditional>

			<Zippy headerTitle="Report Title" isValid={reportTitle.length > 0}>
				<ReportTitleForm projectTitle={projectDetails?.projectTitle} onAdd={setReportTitle} />
			</Zippy>

			<Zippy headerTitle="Cover Sheet" isValid={coverData.coverData.length > 0}>
				<CustomCoverForm initialData={coverData} onAddCoverForm={setCoverData} />
			</Zippy>
			
			<Zippy headerTitle="Executive Summary" isValid={executiveSummary.length > 0}>
				<ExecutiveSummaryForm onProceed={setExecutiveSummary} initialSummary={executiveSummary} />
			</Zippy>

			<Zippy headerTitle="P & ID" isValid={matchPhrases(pidData.hasPID, 'yes') ? pidData.pIDImages.length > 0 : true}>
				<FVIStructurePIDImageForm initialPIDData={pidData} onAdd={setPIDData} />
			</Zippy>

			<Zippy headerTitle="Drawing" isValid={drawing.length > 0}>
				<Drawing initialDrawing={drawing} onAdd={setDrawing} />
			</Zippy>

			<Zippy headerTitle="Infrared Equipment" isValid={infraredEquipmentItems.length > 0}>
				<InfraredEquipmentItems infraredEquipmentItems={infraredEquipmentItems} onChangeInfraredEquipmentItems={setInfraredEquipmentItems} />
			</Zippy>
			
			<Zippy headerTitle="Infrared Results" isValid={infraredResultItems.length > 0}>
				<InfraredResultItems infraredResultItems={infraredResultItems} onChangeInfraredResultItems={setInfraredResultItems} />
			</Zippy>

			<Zippy headerTitle="Inspection Sketch" isValid={sketch.length > 0}>
				<Drawing initialDrawing={sketch} onAdd={setSketch} />
			</Zippy>

			<Zippy headerTitle="Photographic Report" isValid={photographicData.length > 0}>
				<MUTPhotograpicRecord initialImages={photographicData} onProceed={setPhotographicData} />
			</Zippy>

			<div className="d-flex justify-content-between align-items-center">
				<div>
					<Conditional isVisible={autoSave.isProcessing}>
						<LoadingBadge message="Saving" />
					</Conditional>
				</div>

				<Elevation isElevated={activeTutorial === tutorialConstants.PREVIEW_REPORT}>
					<Button
						label="Save and Preview Report"
						disabled={error || autoSave.isProcessing || isSaving}
						onClick={handleSaveAndPreview}
					/>
				</Elevation>
			</div>
		</Fragment>
	);
};

export default CreateCustomInfraredReport;
